import { env } from '$env/dynamic/public';

import type { LoginHttpResponse, RefreshHttpResponse } from './types/Api';

import { resourceVersions, session } from '$lib/PersistedState.svelte';
import type {
  Roaster,
  RoasterTCSettings,
  Roasters,
  RoastingEvent,
  StarredRoast,
  Token,
  UserProfile
} from '$lib/types/RoastLog';

import axios, { type AxiosError, type AxiosResponse } from 'axios';

export const API_HOST = env.PUBLIC_API_HOST || 'https://api.roastlog.com';
console.log(`Using host: ${API_HOST}`);

export const roastUrls = {
  newRoast: `${API_HOST}/api/v1/roasts/`
};

export const baseHeaders = { 'Content-Type': 'application/json' };

export const buildHeaders = (extraHeaders: object = {}) => {
  return {
    ...baseHeaders,
    ...extraHeaders,
    Authorization: `JWT ${session.token}`
  };
};

export const login = async (username: string, password: string): Promise<LoginHttpResponse> => {
  const url = `${API_HOST}/api/v1/jwt-token-auth/`;

  try {
    const response: AxiosResponse = await axios.post(
      url,
      { username, password },
      {
        headers: baseHeaders
      }
    );
    return {
      status: response.status,
      response: response.data
    };
  } catch (error: unknown) {
    const axiosError = error as AxiosError;
    return {
      status: axiosError.response?.status || 500,
      response: axiosError.response?.data || { message: 'An error occurred' }
    };
  }
};

export const tokenRefresh = async (token: Token): Promise<RefreshHttpResponse> => {
  const url = `${API_HOST}/api/v1/api-token-refresh/`;

  try {
    const response = await axios.post(url, { token }, { headers: buildHeaders() });
    return {
      status: response.status,
      response: response.data
    };
  } catch (error: unknown) {
    const axiosError = error as AxiosError;
    const errorMsg = axiosError.message || 'An unknown error occurred';

    return {
      status: axiosError.response?.status || 500,
      response: { error: errorMsg }
    };
  }
};

export const sync = (locationId: number, includeInactive: boolean = false) => {
  const { beans, roasts, roastables, roasters } = resourceVersions.current;

  let url = `${API_HOST}/api/v1/locations/${locationId}/sync/?beans_version=${beans}`;
  url = `${url}&roasts_version=${roasts}&roasters_version=${roasters}`;
  url = `${url}&roastables_version=${roastables}&locations_version=-1`;
  if (includeInactive === true) {
    url = `${url}&inactive_roastables=1&inactive_roasts=1&inactive_beans=1&inactive_roasters=1`;
  }

  return axios.get(url, { headers: buildHeaders() });
};

export const getUserProfile = async (): Promise<UserProfile> => {
  const url = `${API_HOST}/api/v1/me/`;
  const response = await axios.get(url, { headers: buildHeaders() });

  return response.data;
};

export const getStarredRoast = async (roastId: number): Promise<StarredRoast> => {
  const url = `${API_HOST}/api/v2/roasts/${roastId}/`;
  const response = await axios.get(url, { headers: buildHeaders() });

  return response.data;
};

export const getRoasters = async (): Promise<Roasters> => {
  const url = `${API_HOST}/api/v2/roasters/`;
  const response = axios.get(url, { headers: buildHeaders() });

  return (await response).data;
};

export const getRoastingEvents = async (): Promise<RoastingEvent[]> => {
  const url = `${API_HOST}/api/v1/events/`;
  const response = axios.get(url, { headers: buildHeaders() });

  return (await response).data;
};

export const createRoastingEvent = async (roastingEvent: RoastingEvent): Promise<RoastingEvent> => {
  const url = `${API_HOST}/api/v1/events/`;
  const response = axios.post(url, roastingEvent, { headers: buildHeaders() });

  return (await response).data;
};

export const updateRoastingEvent = async (roastingEvent: RoastingEvent): Promise<RoastingEvent> => {
  const url = `${API_HOST}/api/v1/events/${roastingEvent.id}`;
  const response = axios.patch(url, roastingEvent, { headers: buildHeaders() });

  return (await response).data;
};

export const deleteRoastingEvent = async (roastingEventId: number): Promise<RoastingEvent[]> => {
  const url = `${API_HOST}/api/v1/events/${roastingEventId}`;
  const response = axios.delete(url, { headers: buildHeaders() });

  return (await response).data;
};

type RoasterSettingsUpdatePayload = { settings: RoasterTCSettings };

export const updateRoasterSettings = async (
  roasterId: number | string,
  tcSettings: RoasterTCSettings
): Promise<Roaster> => {
  const url = `${API_HOST}/api/v1/roasters/${roasterId}/`;
  const payload: RoasterSettingsUpdatePayload = {
    settings: tcSettings
  };

  try {
    const response: AxiosResponse = await axios.patch(url, payload, {
      headers: buildHeaders()
    });
    return response.data;
  } catch (error: unknown) {
    const axiosError = error as AxiosError<{ message?: string }>;
    throw new Error(axiosError.response?.data?.message || 'An error occurred');
  }
};

export const updateRoast = async (
  roastId: number,
  update: { end_mass?: number; roast_level?: number }
) => {
  const url = `${API_HOST}/api/v2/roasts/${roastId}/`;
  const payload = Object.fromEntries(Object.entries(update).filter(([_, value]) => value != null));
  if (Object.keys(payload).length === 0) {
    return;
  }

  const response: AxiosResponse = await axios.patch(url, payload, {
    headers: buildHeaders()
  });
  return response.data;
};
